import JSZip from 'jszip';

const COMMENTS_RELATIONSHIP =
  'http://schemas.openxmlformats.org/officeDocument/2006/relationships/comments';
const EXCELJS_COMMENT_TARGET = /^\.\.\/comments\d+\.xml$/i;

const parseXml = (xml: string): Document => {
  const document = new DOMParser().parseFromString(xml, 'application/xml');
  if (document.getElementsByTagName('parsererror').length > 0) {
    throw new Error('Invalid XLSX XML.');
  }
  return document;
};

const serializeXml = (document: Document): string =>
  new XMLSerializer().serializeToString(document);

const normalizeArchivePath = (rawPath: string): string => {
  const parts: string[] = [];
  rawPath.replace(/^\/+/, '').split('/').forEach((part) => {
    if (!part || part === '.') return;
    if (part === '..') {
      parts.pop();
      return;
    }
    parts.push(part);
  });
  return parts.join('/');
};

const worksheetPathFromRelationships = (relationshipsPath: string): string =>
  relationshipsPath.replace('/_rels/', '/').replace(/\.rels$/, '');

const resolveRelationshipTarget = (
  relationshipsPath: string,
  target: string,
): string => {
  if (target.startsWith('/')) return normalizeArchivePath(target);
  const worksheetPath = worksheetPathFromRelationships(relationshipsPath);
  const basePath = worksheetPath.slice(0, worksheetPath.lastIndexOf('/') + 1);
  return normalizeArchivePath(`${basePath}${target}`);
};

const cleanContentTypes = async (
  zip: JSZip,
  removedParts: Set<string>,
): Promise<void> => {
  const contentTypes = zip.file('[Content_Types].xml');
  if (!contentTypes) return;

  const document = parseXml(await contentTypes.async('string'));
  Array.from(document.getElementsByTagName('*')).forEach((element) => {
    if (element.localName !== 'Override') return;
    const partName = element.getAttribute('PartName');
    if (partName && removedParts.has(`/${normalizeArchivePath(partName)}`)) {
      element.parentNode?.removeChild(element);
    }
  });
  zip.file('[Content_Types].xml', serializeXml(document));
};

/**
 * Removes only legacy cell-comment relationships that ExcelJS cannot reconcile.
 * Other worksheet relationships, including hyperlinks, are left untouched.
 */
export const stripIncompatibleXlsxComments = async (
  data: ArrayBuffer,
): Promise<ArrayBuffer | null> => {
  const zip = await JSZip.loadAsync(data);
  const relationshipPaths = Object.keys(zip.files).filter(
    (path) => /^xl\/worksheets\/_rels\/[^/]+\.rels$/i.test(path),
  );
  const removedParts = new Set<string>();
  let changed = false;

  for (const relationshipsPath of relationshipPaths) {
    const relationships = zip.file(relationshipsPath);
    if (!relationships) continue;

    const document = parseXml(await relationships.async('string'));
    let relationshipsChanged = false;

    const relationshipElements = Array.from(document.getElementsByTagName('*'));
    for (const relationship of relationshipElements) {
      if (relationship.localName !== 'Relationship'
        || relationship.getAttribute('Type') !== COMMENTS_RELATIONSHIP) continue;

      const target = relationship.getAttribute('Target');
      if (target && EXCELJS_COMMENT_TARGET.test(target)) continue;
      if (target) {
        const archivePath = resolveRelationshipTarget(relationshipsPath, target);
        zip.remove(archivePath);
        removedParts.add(`/${archivePath}`);
      }
      relationship.parentNode?.removeChild(relationship);
      relationshipsChanged = true;
      changed = true;
    }

    if (relationshipsChanged) {
      zip.file(relationshipsPath, serializeXml(document));
    }
  }

  if (!changed) return null;
  await cleanContentTypes(zip, removedParts);
  return zip.generateAsync({ type: 'arraybuffer', compression: 'DEFLATE' });
};
